import {Cookies, useCookies} from "react-cookie"
import {HOSTNAME} from "../constants"
import {productionMode} from "../utils/routes"

/**
 * The signed-in session cookie, shared by the guest site and the app.
 *
 * In production it's set on `.linguascores.com`, so linguascores.com and app.linguascores.com read
 * and write the same session: signing in, editing the profile or logging out on one shows on the
 * other. It used to be host-only, so each site kept its own copy and they drifted apart.
 *
 * The test host lives under the same domain, so it uses its own cookie name; otherwise it would
 * pick up production sessions. Local dev keeps a host-only cookie under the usual name.
 */
const isTestHost = window.location.hostname.startsWith("test") && window.location.hostname.endsWith(HOSTNAME)

export const SESSION_COOKIE = isTestHost ? "linguascore_user_session_test" : "linguascore_user_session"

const sharedOptions = (expires?: Date) => ({
    path: "/",
    ...(productionMode && {domain: `.${HOSTNAME}`}),
    ...(expires && {expires}),
})

/** The pre-sharing cookie, tied to the exact host that set it. */
const hostOnlyOptions = {path: "/"}

export const useSessionCookie = () => {
    const [cookies, setCookie, removeCookie] = useCookies([SESSION_COOKIE])

    return {
        userSession: cookies[SESSION_COOKIE],
        setUserSession: (value: object, expires?: Date) => {
            // Drop any host-only copy first, so the browser never holds two cookies of this name.
            if (productionMode) removeCookie(SESSION_COOKIE, hostOnlyOptions)
            setCookie(SESSION_COOKIE, value, sharedOptions(expires))
        },
        clearUserSession: () => {
            removeCookie(SESSION_COOKIE, sharedOptions())
            if (productionMode) removeCookie(SESSION_COOKIE, hostOnlyOptions)
        },
    }
}

/**
 * One-off move of a session saved before the cookie was shared: re-saves it on the shared domain
 * and deletes the host-only copy. Runs before the app renders (see index.tsx).
 */
export const migrateSessionCookie = () => {
    if (!productionMode) return
    try {
        const cookies = new Cookies()
        const session = cookies.get(SESSION_COOKIE)
        if (!session) return
        cookies.remove(SESSION_COOKIE, hostOnlyOptions)
        const expires = session.expiresAt ? new Date(session.expiresAt) : undefined
        cookies.set(SESSION_COOKIE, session, sharedOptions(expires))
    } catch (e) {
        console.log(e)
    }
}
